"use client";

import Image from "./image";
import { Bot, MapPin, Phone, Send, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import {
  memo,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { siteConfig } from "../lib/site-config";
import {
  type CustomerChatMessage,
  useCustomerChat,
} from "./use-customer-chat";

function cleanMarkdownText(text: string) {
  return text.replace(/\*+/g, "").trim();
}

function parseTableRow(line: string) {
  return line
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map(cleanMarkdownText);
}

function isTableSeparator(line: string) {
  const cells = parseTableRow(line);
  return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function isListItem(line: string) {
  return /^\s*[-+*]\s+/.test(line);
}

const ChatReply = memo(function ChatReply({ content }: { content: string }) {
  const lines = content.split(/\r?\n/);
  const blocks: ReactNode[] = [];
  let textLines: string[] = [];

  const addText = () => {
    const text = cleanMarkdownText(textLines.join("\n"));
    if (text) blocks.push(<p key={`text-${blocks.length}`}>{text}</p>);
    textLines = [];
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];

    if (!line.trim()) {
      addText();
      continue;
    }

    const header = parseTableRow(line);

    if (line.includes("|") && isTableSeparator(lines[index + 1] ?? "")) {
      addText();
      const rows: string[][] = [];
      index += 2;

      while (index < lines.length && lines[index].includes("|")) {
        rows.push(parseTableRow(lines[index]));
        index += 1;
      }

      blocks.push(
        <div className="customer-chat-table" key={`table-${blocks.length}`}>
          <table>
            <thead>
              <tr>
                {header.map((cell, cellIndex) => (
                  <th key={cellIndex}>{cell}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {header.map((_, cellIndex) => (
                    <td key={cellIndex}>{row[cellIndex] ?? ""}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );

      index -= 1;
      continue;
    }

    if (isListItem(line)) {
      addText();
      const items: string[] = [];

      while (index < lines.length && isListItem(lines[index])) {
        items.push(cleanMarkdownText(lines[index].replace(/^\s*[-+*]\s+/, "")));
        index += 1;
      }

      blocks.push(
        <ul className="customer-chat-list" key={`list-${blocks.length}`}>
          {items.map((item, itemIndex) => (
            <li key={itemIndex}>{item}</li>
          ))}
        </ul>,
      );

      index -= 1;
      continue;
    }

    textLines.push(line);
  }

  addText();
  return <div className="customer-chat-reply">{blocks}</div>;
});

const ChatMessageList = memo(function ChatMessageList({
  messages,
  messagesEndRef,
}: {
  messages: CustomerChatMessage[];
  messagesEndRef: RefObject<HTMLDivElement | null>;
}) {
  return (
    <div className="customer-chat-messages" aria-live="polite">
      {messages.map((message) => (
        <div
          className={`customer-chat-message is-${message.sender}`}
          key={message.id}
        >
          {message.sender === "advisor" && (
            <span className="customer-chat-avatar">
              <Bot size={16} />
            </span>
          )}
          <div>
            {message.sender === "advisor" && (
              <span className="customer-chat-sender">
                {siteConfig.chat.serviceName}
              </span>
            )}
            {message.sender === "advisor" ? (
              <ChatReply content={message.content} />
            ) : (
              <p>{message.content}</p>
            )}
            <time>
              {message.timestamp.toLocaleTimeString("zh-CN", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </time>
          </div>
        </div>
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
});

export function CustomerChat({
  open,
  onClose,
  initialPrompt,
}: {
  open: boolean;
  onClose: () => void;
  initialPrompt?: string;
}) {
  const [input, setInput] = useState("");
  const { messages, sendMessage, isSending } = useCustomerChat();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && messages.length > 1)
      messagesEndRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length, open]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, open]);

  useEffect(() => {
    if (open) setInput(initialPrompt ?? "");
  }, [initialPrompt, open]);

  const handleSend = () => {
    if (!input.trim() || isSending) return;
    sendMessage(input);
    setInput("");
  };

  const handleQuickQuestion = (question: string) => {
    if (!isSending) sendMessage(question);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="customer-chat-overlay"
          role="presentation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.14 }}
          onMouseDown={onClose}
        >
          <motion.section
            className="customer-chat"
            role="dialog"
            aria-modal="true"
            aria-labelledby="customer-chat-title"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="customer-chat-header">
              <div className="customer-chat-title">
                <span className="customer-chat-mark">
                  <Bot size={19} />
                </span>
                <div>
                  <h2 id="customer-chat-title">{siteConfig.chat.title}</h2>
                  <span>{siteConfig.chat.serviceName}在线</span>
                </div>
              </div>
            </header>
            <button
              className="customer-chat-close"
              type="button"
              onClick={onClose}
              aria-label="关闭在线咨询"
              title="关闭"
            >
              <X size={19} />
            </button>
            <div className="customer-chat-main">
              <ChatMessageList
                messages={messages}
                messagesEndRef={messagesEndRef}
              />
              <div className="customer-chat-composer">
                <textarea
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (
                      event.key === "Enter" &&
                      !event.shiftKey &&
                      !event.nativeEvent.isComposing
                    ) {
                      event.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder={siteConfig.chat.placeholder}
                  aria-label={siteConfig.chat.placeholder}
                />
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!input.trim() || isSending}
                  aria-label="发送消息"
                  title="发送消息"
                >
                  <Send size={19} />
                </button>
              </div>
            </div>
            <aside className="customer-chat-info" aria-label="联系信息">
              <Image
                src={siteConfig.contact.qrCode.src}
                alt={siteConfig.contact.qrCode.alt}
                width={144}
                height={144}
              />
              <strong>{siteConfig.contact.wechat}</strong>
              <span className="customer-chat-info-label">微信沟通</span>
              <p>
                <Phone size={15} />
                {siteConfig.contact.phone.display}
              </p>
              <p>
                <MapPin size={15} />
                {siteConfig.contact.address}
              </p>
              <div className="customer-chat-faq" aria-label="常见问题">
                <span>常见问题</span>
                <div>
                  {siteConfig.chat.faqs.map((faq) => (
                    <button
                      key={faq.question}
                      type="button"
                      onClick={() => handleQuickQuestion(faq.question)}
                      disabled={isSending}
                    >
                      {faq.question}
                    </button>
                  ))}
                </div>
              </div>
            </aside>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
